import { Tooltip, ActionIcon } from '@mantine/core'
import dayjs from 'dayjs'
import { VerificationOfEmployment } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'

import DnTable from 'src/components/Core/DnTable'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { VoEStatus } from 'src/graphql/types/verificationOfEmployments'
import IconCalendar from 'src/icons/IconCalendar'
import IconCopy from 'src/icons/IconCopy'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import IconRightArrow from 'src/icons/IconRightArrow'
import { calculateAverageJobDuration } from 'src/lib/employment.utils'
import { formatSnakeValue } from 'src/lib/formatters'
import { toast } from 'src/components/Overlays/Toast/Toast'

export const colorByStatus: Record<
  VoEStatus,
  'gray' | 'blue' | 'green' | 'orange' | 'red'
> = {
  NOT_STARTED: 'gray',
  REQUESTED: 'blue',
  PENDING_REVIEW: 'orange',
  COMPLETED: 'green',
  IN_PROGRESS: 'blue',
  CANCELED: 'red',
  DECLINED: 'red',
}

export interface VoERequestsTableProps {
  loading: boolean
  voeList: VerificationOfEmployment[]
  totalCount: number
  onLoadPage: (pageInfo: { offset: number; limit: number }) => void
}

const DUE_DATE_LIMIT_DAYS = 30
const DUE_DATE_ALERT_DAYS = 10

const getDaysTillDueDate = (createdAtDate) => {
  const dueDate = new Date(
    createdAtDate.getTime() + DUE_DATE_LIMIT_DAYS * 1000 * 60 * 60 * 24
  )
  return Math.ceil(
    (dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  )
}

const VoERequestsTable = ({
  loading,
  voeList,
  totalCount,
  onLoadPage,
}: VoERequestsTableProps) => {
  const onAction = (voe: VerificationOfEmployment) => {
    navigate(routes.voeRequestDetails({ id: voe.voeId }))
  }

  const columns = [
    {
      key: 'id',
      label: 'ID',
      width: 3,
      render: (voe: VerificationOfEmployment) => (
        <Tooltip label={voe.voeId} position="top">
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(voe.voeId)
              toast('ID copied', 'success')
            }}
          >
            <IconCopy className="h-4 w-4 fill-none stroke-doubleNickel-gray-400 hover:stroke-doubleNickel-gray-700" />
          </button>
        </Tooltip>
      ),
    },
    {
      key: 'driverName',
      label: 'Name',
      width: 16,
      render: (voe: VerificationOfEmployment) => (
        <div className="flex w-full flex-row items-center justify-between whitespace-nowrap">
          <Tooltip label={voe.driverName || ''} position="top" openDelay={300} disabled={!voe.driverName}>
            <div className="max-w-52 truncate font-medium text-doubleNickel-gray-900">
              {voe.driverName || ''}
            </div>
          </Tooltip>
          {voe.applicant && (
            <Tooltip label="Go to profile">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation()
                  navigate(
                    routes.applicantDetails({
                      id: voe.applicant.applicantId,
                      tab: 'application',
                    })
                  )
                }}
                className="flex-shrink-0"
              >
                <IconRightArrow className="h-3 fill-none stroke-doubleNickel-brand-500" />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'companyName',
      label: 'Company Name',
      width: 16,
      render: (voe: VerificationOfEmployment) => (
        <Tooltip label={voe.companyName} position="top" openDelay={300}>
          <div className="max-w-48 truncate text-xs text-doubleNickel-gray-900">
            {voe.companyName}
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 10,
      render: (voe: VerificationOfEmployment) => (
        <StyledBadge
          textTransform="capitalize"
          color={colorByStatus[voe.status]}
        >
          {formatSnakeValue(voe.status)}
        </StyledBadge>
      ),
    },
    {
      key: 'startDate',
      label: (
        <Tooltip label="Employment Start Date">
          <p>Start Date</p>
        </Tooltip>
      ),
      width: 10,
      render: (voe: VerificationOfEmployment) =>
        voe.requestContent.startDate
          ? dayjs(voe.requestContent.startDate).utc().format('MM/DD/YYYY')
          : '-',
    },
    {
      key: 'endDate',
      label: (
        <Tooltip label="Employment End Date">
          <p>End Date</p>
        </Tooltip>
      ),
      width: 10,
      render: (voe: VerificationOfEmployment) =>
        voe.requestContent.endDate
          ? dayjs(voe.requestContent.endDate).utc().format('MM/DD/YYYY')
          : '-',
    },
    {
      key: 'jobDuration',
      label: (
        <Tooltip label="Job Duration">
          <p>Job Duration</p>
        </Tooltip>
      ),
      width: 10,
      render: (voe: VerificationOfEmployment) =>
        calculateAverageJobDuration([voe.requestContent]),
    },
    {
      key: 'createdAt',
      label: 'Created At',
      width: 12,
      render: (voe: VerificationOfEmployment) => {
        const daysTillDueDate = getDaysTillDueDate(new Date(voe.createdAt))
        const showDueDateIcon = daysTillDueDate <= DUE_DATE_ALERT_DAYS
        const isPastDue = daysTillDueDate < 0

        const message = isPastDue
          ? `${Math.abs(daysTillDueDate)} day${
              Math.abs(daysTillDueDate) > 1 ? 's' : ''
            } past due`
          : `Due in ${daysTillDueDate} day${daysTillDueDate > 1 ? 's' : ''}`

        return (
          <div>
            <div className="flex flex-row items-center justify-between">
              {voe.createdAt ? dayjs(voe.createdAt).format('MM/DD/YYYY') : '-'}
              {showDueDateIcon && voe.status != VoEStatus.COMPLETED && (
                <Tooltip label={message}>
                  <div>
                    <IconCalendar
                      className={`h-4 fill-none ${
                        isPastDue
                          ? 'stroke-doubleNickel-error-600'
                          : 'stroke-doubleNickel-warning-500'
                      }`}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        )
      },
    },
  ]

  return (
    <DnTable
      columns={columns}
      data={voeList || []}
      loading={loading}
      totalCount={totalCount}
      onLoadPage={onLoadPage}
      defaultResultsPerPage={20}
      onRowClick={(index, rowData) => onAction(rowData)}
      emptyStateMessage="No incoming employment verifications found"
      emptyStateIcon={<IconEmptyInbox className="h-40 fill-none" />}
    />
  )
}

export default VoERequestsTable
